import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/** Base skeleton styling used across analytics and dashboard panels. */
export const skeletonBaseClass = "rounded-md bg-muted";

interface MetricSkeletonProps {
  className?: string;
}

/** Single metric value placeholder (analytics stat cards). */
export function MetricSkeleton({ className }: MetricSkeletonProps) {
  return (
    <Skeleton className={cn("mr-auto h-8 w-20", skeletonBaseClass, className)} />
  );
}

interface TextSkeletonProps {
  className?: string;
}

/** Single-line text placeholder. */
export function TextSkeleton({ className }: TextSkeletonProps) {
  return (
    <Skeleton className={cn("mr-auto h-4 w-3/4", skeletonBaseClass, className)} />
  );
}

interface WidgetContentSkeletonProps {
  /** List-style body rows below the optional summary strip. */
  rows?: number;
  /** Top summary / badge strip. */
  showSummary?: boolean;
  className?: string;
}

/**
 * Card-body loading state for dashboard widgets — matches analytics panel skeletons.
 */
export function WidgetContentSkeleton({
  rows = 3,
  showSummary = true,
  className,
}: WidgetContentSkeletonProps) {
  return (
    <div className={cn("space-y-3", className)}>
      {showSummary && (
        <Skeleton className={cn("h-10 w-full", skeletonBaseClass)} />
      )}
      {Array.from({ length: rows }).map((_, index) => (
        <TextSkeleton key={index} />
      ))}
    </div>
  );
}

interface TableSkeletonProps {
  title?: string;
  rows?: number;
  columns?: number;
  className?: string;
}

/** Table loading state (analytics reports). */
export function TableSkeleton({
  title,
  rows = 5,
  columns = 4,
  className,
}: TableSkeletonProps) {
  return (
    <div className={cn("overflow-hidden rounded-xl border border-border bg-card text-sm", className)}>
      {title ? (
        <div className="border-b border-border px-3 py-2 text-right text-xs font-medium text-foreground">
          {title}
        </div>
      ) : (
        <div className="border-b border-border px-3 py-2">
          <Skeleton className={cn("mr-auto h-4 w-40", skeletonBaseClass)} />
        </div>
      )}
      <table className="min-w-full divide-y divide-border text-xs">
        <thead className="bg-muted/30 text-[11px] text-muted-foreground">
          <tr>
            {Array.from({ length: columns }).map((_, index) => (
              <th key={index} className="px-3 py-2">
                <Skeleton className={cn("mx-auto h-3 w-16", skeletonBaseClass)} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <td key={colIndex} className="px-3 py-2">
                  <Skeleton
                    className={cn(
                      "h-3",
                      skeletonBaseClass,
                      colIndex === 0 ? "mr-auto w-24" : "mx-auto w-12",
                    )}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
